import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { DialogAddCommentData } from 'src/app/components/dialogs/add-comment/models/dialog-add-comment-data';
import { CommentInput } from 'src/app/models/server/comment-input';
import { Contact } from 'src/app/models/server/contact';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { FolderWorkflowStepsSnapshot } from 'src/app/models/server/folder-workflow-steps-snapshot';
import { History } from 'src/app/models/server/history';
import { Reminder } from 'src/app/models/server/reminder';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';
import { ArchivedFoldersService } from 'src/app/services/http/archived-folders.service';
import { DetailBaseDatasource } from './detail-base-datasource';

@Injectable()
export class ArchiveDetailDatasource extends DetailBaseDatasource {
    constructor(protected archivedFoldersService: ArchivedFoldersService) {
        super();
    }

    /**
     * Supprime le dossier spécifié.
     */
    public override deleteFolder(folder: Folder) {
        throw new Error('Not implemented.');
    }

    /**
     * Place le dossier spécifié en veille.
     */
    public override setFolderInStandByState(folder: Folder, data: StandByDatas) {
        throw new Error('Not implemented.');
    }

    /**
     * Sort le dossier spécifié de la veille
     */
    public override removeFolderFromStandByState(folder: Folder) {
        throw new Error('Not implemented.');
    }

    /**
     * Place le dossier spécifié en 'attente d'archivage'.
     */
    public override setFolderInWaitingArchivingState(folder: Folder): void {
        throw new Error('Not implemented.');
    }

    /**
     * Retire le dossier spécifié de l'état en 'attente d'archivage'.
     */
    public override removeFolderFromWaitingArchivingState(folder: Folder): void {
        throw new Error('Not implemented.');
    }

    /**
     * Liste les documents d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listDocuments(viewId: number, folderId: string): Observable<FolderDocument[]> {
        return this.archivedFoldersService.get(viewId, folderId).pipe(map((x) => x.documents));
    }

    /**
     * Supprime le document spécifié.
     */
    public override deleteDocument(viewId: number, folderId: string, documentId: string): Observable<boolean> {
        throw new Error('Not implemented.');
    }

    /**
     * Ajoute un commentaire au dossier.
     */
    public override addComment(viewId: number, folderId: string, data: DialogAddCommentData): Observable<FolderDocument> {
        throw new Error('Not implemented.');
    }

    /**
     * Met à jour un commentaire d'un dossier.
     */
    public override updateComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        throw new Error('Not implemented.');
    }

    /**
     * Supprime un commentaire d'un dossier.
     * @param viewId
     * @param reminder
     */
    public override deleteComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument> {
        throw new Error('Not implemented.');
    }

    /**
     * Ajoute un rappel au dossier.
     */
    public override addReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder> {
        throw new Error('Not implemented.');
    }

    /**
     * Met à jour un rappel d'un dossier.
     */
    public override updateReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder> {
        throw new Error('Not implemented.');
    }

    /**
     * Supprime un rappel d'un dossier.
     * @param viewId
     * @param reminder
     */
    public override deleteReminder(viewId: number, reminder: Reminder): Observable<boolean> {
        throw new Error('Not implemented.');
    }

    /**
     * Liste les rappels d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listReminders(viewId: number, folderId: string): Observable<Reminder[]> {
        return of([]);
    }

    /**
     * Liste les entrées de l'historique d'un dossier.
     * @param viewId
     * @param folderId
     */
    public override listHistories(viewId: number, folderId: string): Observable<History[]> {
        return this.archivedFoldersService.get(viewId, folderId).pipe(map((x) => x.histories));
    }

    /**
     * Marque un document comme 'lu' ou comme 'non lu'.
     * @param viewId
     * @param folderId
     * @param documentId
     * @param isRead
     */
    public override markDocumentAsRead(viewId: number, folderId: string, documentId: string, isRead: boolean): Observable<FolderDocument> {
        throw new Error('Not implemented.');
    }

    /**
     * Marque un dossier comme 'lu' ou comme 'non lu'.
     * @param folder
     * @param isRead
     */
    public override markFolderAsRead(folder: Folder, isRead: boolean) {
        throw new Error('Not implemented.');
    }

    /**
     * Ajoute un contact au carnet d'adresses.
     * @param contact
     */
    public override addContact(contact: Contact) {
        throw new Error('Not implemented.');
    }

    /**
     * Déplace un dossier dans une bannette.
     * @param viewId
     * @param folder
     * @param boxId
     */
    public override moveFolderToBox(viewId: number, folder: Folder, boxId: string) {
        throw new Error('Not implemented.');
    }

    /**
     * Met à jour un attribut d'un dossier.
     * @param viewId
     * @param folder
     * @param attributeId
     * @param oldValue
     * @param newValue
     */
    public override updateFolderAttribute(viewId: number, folder: Folder, attributeId: number, oldValue: any, newValue: any) {
        throw new Error('Not implemented.');
    }

    /**
     * Met à jour l'état d'une étape du workflow.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     * @param newStateId
     */
    public override updateFolderWorkflowStepState(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot, newStateId: number) {
        throw new Error('Not implemented.');
    }

    /**
     * Active une étape de workflow d'un dossier.
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    public override activateFolderWorkflowStep(viewId: number, folderId: string, workflowsStepId: number): void {
        throw new Error('Not implemented.');
    }

    /**
     * Finalise une étape de workflow d'un dossier.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     */
    public override completeFolderWorkflowStep(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot): void {
        throw new Error('Not implemented.');
    }

    /**
     * Archive le dossier.
     * @param folder Instance du dossier.
     */
    public override archive(folder: Folder): void {
        throw new Error('Not implemented.');
    }

    /**
     * Suit le dossier.
     * @param folder Instance du dossier.
     */
    public override followFolder(folder: Folder): void {
        throw new Error('Not implemented.');
    }

    /**
     * Arrête le suivi du dossier.
     * @param folder Instance du dossier.
     */
    public override stopFollowFolder(folder: Folder): void {
        throw new Error('Not implemented.');
    }
}
